import { useState, useCallback, useRef, MutableRefObject } from "react"

export interface ElementDimensions {
	clientWidth: number
	clientHeight: number
	scrollWidth: number
	scrollHeight: number
}

export interface UseResizeObserverResponse {
	observeRef: (target: HTMLElement | null) => void
	dimensions: ElementDimensions
	element: MutableRefObject<HTMLElement | null>
}

/**
 * @returns ref to pass to the target element, ElementDimensions
 */
export const useResizeObserver = (
	options: { observeChildren?: boolean } = {},
): UseResizeObserverResponse => {
	const [dimensions, setDimensions] = useState<ElementDimensions>({} as ElementDimensions)
	const observer = useRef<ResizeObserver | null>(null) // we only need one observer instance
	const element = useRef<HTMLElement | null>(null)

	const handleResize = useCallback((entries: ResizeObserverEntry[]) => {
		const target = element.current

		if (!Array.isArray(entries) || !target) {
			return
		}

		const newDimensions: ElementDimensions = {
			clientWidth: target.clientWidth,
			clientHeight: target.clientHeight,
			scrollWidth: target.scrollWidth,
			scrollHeight: target.scrollHeight,
		}

		setDimensions(newDimensions)
	}, [])

	// initialize resize observer
	const observeRef = useCallback(
		(target: HTMLElement | null) => {
			// the callback ref fires often without a target, so only process when we have a target
			if (!target) {
				return
			}

			// instantiate a new observer if needed
			if (!observer.current) {
				observer.current = new ResizeObserver((entries) => handleResize(entries))
			}

			// monitor the new element with cleanup of the old element
			if (element.current !== target) {
				element.current && observer.current?.disconnect() // call disconnect if monitoring old element
				observer.current.observe(target)
				element.current = target

				if (options.observeChildren) {
					for (const child of target.children) {
						observer.current?.observe(child)
					}
				}
			}
		},
		[handleResize],
	)

	return { observeRef, dimensions, element }
}
